import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem, QuestionConfigQuestionType } from '/index';

import { basketTableTotalTypes, questionScopeCategories } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';
import vehicleIcons from '/shared/util/vehicleIcons';

import CommuterFactorCanonicalName from '/business/shared/components/widgets/misc/CommuterFactorCanonicalName';
import DistanceCanonicalName from '/business/shared/components/widgets/misc/DistanceCanonicalName';

import commutesCardBackground from '/business/shared/images/initiatives/Employees-commute-1x.jpg';
import commutesBackground from '/business/shared/images/initiatives/Employees-commute-2x.jpg';

import {
  BOTTOM_COMMUTES_ACTIVITY,
  BOTTOM_COMMUTES_ALL,
  BOTTOM_COMMUTES_WORKFORCE,
} from '/business/shared/components/questions/scope3/employeeCommutes/_education/bottomEmployeeCommutes';
import educationContentJson from '/business/shared/components/questions/scope3/employeeCommutes/_education/employeeCommutes.json';
import useEmployeeCommutesAccurateSchema from '/business/shared/components/questions/scope3/employeeCommutes/_hooks/useEmployeeCommutesAccurateSchema';
import useEmployeeCommutesEstimateSchema from '/business/shared/components/questions/scope3/employeeCommutes/_hooks/useEmployeeCommutesEstimateSchema';

const ACCURATE_API_KEY = 'calculation-travel-emissions-employee-commutes-accurate';
const ESTIMATE_API_KEY = 'calculation-travel-emissions-employee-commutes-estimate';
const EMPTY_CELL = '—';
const EDUCATION = {
  hasEducationWidget: true,
  educationContent: educationContentJson,
  bottomContentsAll: BOTTOM_COMMUTES_ALL,
};
const EDUCATION_WORKFORCE = { ...EDUCATION, bottomContentsAll: BOTTOM_COMMUTES_WORKFORCE };
const EDUCATION_ACTIVITY = { ...EDUCATION, bottomContentsAll: BOTTOM_COMMUTES_ACTIVITY };

type ConfigureEmployeeCommutesOptions = {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
  vehicleIcon?: string;
};

type EmployeeCommuteAccurateTableData = {
  commuterFactor?: string | null;
  distance?: string | number | null;
  distanceUnit?: string | null;
  employmentType?: string | null;
  tonnage?: number | null;
};

type EmployeeCommuteEstimateTableData = EmployeeCommuteAccurateTableData & {
  numOfEmployees?: string | number | null;
};

type EmployeeCommuteSummaryRow = BasketTableAnswerItem & {
  summaryCells: BasketTableAnswerItem['cells'];
};

const getEmploymentTypeLabel = (i18n: I18nInstance, employmentType?: string | null) => {
  const resolved = employmentType === 'part_time' ? 'part_time' : 'full_time';
  return i18n.t(`business:QuestionPage.forms.commutes.employmentType.${resolved}`);
};

const getNumOfEmployees = (item: BasketTableAnswerItem) => Number(item.answer?.genericData?.numOfEmployees ?? 0) || 0;

const SUMMARY_MODE_ORDER = ['driving', 'bus', 'train', 'motorcycle', 'moped_scooter'];

const getSummaryModeIndex = (item: BasketTableAnswerItem) => {
  const { commuterFactor } = (item.answer?.genericData || {}) as EmployeeCommuteEstimateTableData;
  const index = SUMMARY_MODE_ORDER.indexOf(commuterFactor ?? '');
  return index === -1 ? SUMMARY_MODE_ORDER.length : index;
};

const countEmployees = (items: BasketTableAnswerItem[]) =>
  items.reduce((total, item) => (item.type === ESTIMATE_API_KEY ? total + getNumOfEmployees(item) : total + 1), 0);

export const getConfigureEmployeeCommutes = ({
  i18n,
  hasScopeBasedOrganisation = true,
  vehicleIcon = vehicleIcons.busStop,
  reductionCardsSlides = ['slide1', 'slide2', 'slide3', 'slide4', 'slide5'],
}: ConfigureEmployeeCommutesOptions): QuestionConfigQuestionType => {
  const scopeCategory = hasScopeBasedOrganisation
    ? questionScopeCategories.SCOPE_3
    : questionScopeCategories.TRAVEL_EMISSIONS;
  const commutesInitiative = {
    getVisuals: () => {
      return {
        description: false,
        background: commutesBackground,
        useMask: false,
        cardBackground: commutesCardBackground,
      };
    },
    formatForInitiativeSummary: () => {
      return {
        isShowingReportingPeriod: true,
        isShowingReportingPeriodNoReduction: false,
        description: 'Distance travelled in km during the reporting period ',
        descriptionNoReduction: false,
      };
    },
  };
  return {
    id: 'employee-commutes',
    scopeCategory,
    title: i18n.t('business:Questions.travel.question3.title'),
    icon: vehicleIcon,
    isCompletePropName: 'isEmployeeCommutesComplete',
    primaryI18nKey: 'business:QuestionPage.forms.travelEmissionsSelector.para7',
    subTextI18nKey: 'business:QuestionPage.forms.travelEmissionsSelector.para8',
    overviewPageTitleI18nKey: 'business:Questions.travel.question3.title',
    form: import('/business/shared/components/questions/scope3/employeeCommutes/EmployeeCommutesLandingQuestion'),
    reductionPageKey: 'employeeCommutes',
    reductionPageCards: reductionCardsSlides,
    selector: {
      tableTabs: [
        {
          label: i18n.t('business:QuestionPage.forms.commutes.individualEntriesTab'),
          apiKey: ACCURATE_API_KEY,
          emptyLabel: 'individual',
        },
        {
          label: i18n.t('business:QuestionPage.forms.commutes.groupedEntriesTab'),
          apiKey: ESTIMATE_API_KEY,
          emptyLabel: 'grouped',
        },
      ],
      tableViewSummaryHeadings: [
        i18n.t('business:Questions.travel.question3.subQuestion2.table.heading1'),
        i18n.t('business:Questions.travel.question3.subQuestion2.table.heading2'),
        i18n.t('business:Questions.travel.question3.subQuestion2.table.heading3'),
        i18n.t('business:Questions.travel.question3.subQuestion2.table.heading4'),
        'Carbon emissions',
      ],
      tableViewSummaryMeta: (items: BasketTableAnswerItem[]) =>
        i18n.t('business:QuestionPage.forms.commutes.totalEmployees', { total: countEmployees(items) }),
      tableViewSummaryRowParser: (items: BasketTableAnswerItem[]) => {
        const groups = new Map<
          string,
          { item: BasketTableAnswerItem; fullTime: number; partTime: number; tonnage: number }
        >();
        for (const item of items) {
          const genericData = (item.answer?.genericData || {}) as EmployeeCommuteEstimateTableData;
          const key = `${genericData.commuterFactor}|${genericData.distance}|${genericData.distanceUnit}`;
          const group = groups.get(key) ?? { item, fullTime: 0, partTime: 0, tonnage: 0 };
          const employees = item.type === ESTIMATE_API_KEY ? getNumOfEmployees(item) : 1;
          if (genericData.employmentType === 'part_time') {
            group.partTime += employees;
          } else {
            group.fullTime += employees;
          }
          group.tonnage += item.answer?.tonnage || 0;
          groups.set(key, group);
        }
        return [...groups.entries()]
          .sort(([, a], [, b]) => getSummaryModeIndex(a.item) - getSummaryModeIndex(b.item))
          .map(([key, group]): EmployeeCommuteSummaryRow => {
            const genericData = (group.item.answer?.genericData || {}) as EmployeeCommuteEstimateTableData;
            return {
              ...group.item,
              itemId: `summary-${key}`,
              summaryCells: [
                <CommuterFactorCanonicalName key="mode" commuterFactor={genericData.commuterFactor} />,
                <DistanceCanonicalName
                  key="distance"
                  distance={genericData.distance ?? ''}
                  unit={genericData.distanceUnit ?? ''}
                  type="daily"
                />,
                group.fullTime === 0 ? EMPTY_CELL : `${group.fullTime}`,
                group.partTime === 0 ? EMPTY_CELL : `${group.partTime}`,
                `${formatNumber(group.tonnage)} tCO₂e`,
              ],
            };
          });
      },
    },
    subQuestions: [
      {
        id: 'workforce-breakdown',
        title: i18n.t('business:QuestionPage.forms.commutesWorkforce.title'),
        breadcrumbTitle: i18n.t('business:QuestionPage.forms.commutesWorkforce.breadcrumbTitle'),
        scopeCategory,
        form: import(
          '/business/shared/components/questions/scope3/employeeCommutes/EmployeeCommutesWorkforceBreakdownQuestion'
        ),
        education: EDUCATION_WORKFORCE,
      },
      {
        id: 'activity-data',
        title: i18n.t('business:QuestionPage.forms.commutesActivityData.title'),
        breadcrumbTitle: i18n.t('business:QuestionPage.forms.commutesActivityData.breadcrumbTitle'),
        scopeCategory,
        form: import(
          '/business/shared/components/questions/scope3/employeeCommutes/EmployeeCommutesActivityDataQuestion'
        ),
        education: EDUCATION_ACTIVITY,
        subQuestions: [
          {
            id: 'individual-entries',
            title: i18n.t('business:Questions.travel.question3.subQuestion1.title'),
            breadcrumbTitle: i18n.t('business:Questions.travel.question3.subQuestion1.breadcrumbTitle'),
            apiKey: ACCURATE_API_KEY,
            scopeCategory,
            useHookLoader: useEmployeeCommutesAccurateSchema,
            table: {
              title: i18n.t('business:Questions.travel.question3.subQuestion1.table.title'),
              calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
              totalType: basketTableTotalTypes.COUNT,
              siblingQuestionKeys: [ESTIMATE_API_KEY],
              headings: [
                i18n.t('business:Questions.travel.question3.subQuestion1.table.heading1'),
                i18n.t('business:Questions.travel.question3.subQuestion1.table.heading2'),
                i18n.t('business:Questions.travel.question3.subQuestion1.table.heading3'),
                i18n.t('business:Questions.travel.question3.subQuestion1.table.heading4'),
                'Carbon emissions',
              ],
              cells: [
                'firstName',
                ({ commuterFactor }: EmployeeCommuteAccurateTableData) => (
                  <CommuterFactorCanonicalName commuterFactor={commuterFactor} />
                ),
                ({ distance, distanceUnit }) => (
                  <DistanceCanonicalName distance={distance} unit={distanceUnit} type="daily" />
                ),
                ({ employmentType }: EmployeeCommuteAccurateTableData) => getEmploymentTypeLabel(i18n, employmentType),
                ({ tonnage }: EmployeeCommuteAccurateTableData) => `${formatNumber(tonnage)} tCO₂e`,
              ],
              isAnswerEditable: true,
            },
            editPageTitle: i18n.t('business:Questions.travel.question3.subQuestion1.editTitle'),
            details: {},
            initiative: commutesInitiative,
            education: EDUCATION_ACTIVITY,
          },
          {
            id: 'grouped-entries',
            title: i18n.t('business:Questions.travel.question3.subQuestion2.title'),
            breadcrumbTitle: i18n.t('business:Questions.travel.question3.subQuestion2.breadcrumbTitle'),
            apiKey: ESTIMATE_API_KEY,
            useHookLoader: useEmployeeCommutesEstimateSchema,
            scopeCategory,
            table: {
              title: i18n.t('business:Questions.travel.question3.subQuestion2.table.title'),
              calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
              totalType: basketTableTotalTypes.COUNT,
              siblingQuestionKeys: [ACCURATE_API_KEY],
              headings: [
                i18n.t('business:Questions.travel.question3.subQuestion2.table.heading1'),
                i18n.t('business:Questions.travel.question3.subQuestion2.table.heading2'),
                i18n.t('business:Questions.travel.question3.subQuestion2.table.heading3'),
                i18n.t('business:Questions.travel.question3.subQuestion2.table.heading4'),
                'Carbon emissions',
              ],
              cells: [
                ({ commuterFactor }: EmployeeCommuteEstimateTableData) => (
                  <CommuterFactorCanonicalName commuterFactor={commuterFactor} />
                ),
                ({ distance, distanceUnit }) => (
                  <DistanceCanonicalName distance={distance} unit={distanceUnit} type="daily" />
                ),
                ({ employmentType, numOfEmployees }: EmployeeCommuteEstimateTableData) =>
                  employmentType === 'part_time' ? EMPTY_CELL : `${numOfEmployees}`,
                ({ employmentType, numOfEmployees }: EmployeeCommuteEstimateTableData) =>
                  employmentType === 'part_time' ? `${numOfEmployees}` : EMPTY_CELL,
                ({ tonnage }: EmployeeCommuteEstimateTableData) => `${formatNumber(tonnage)} tCO₂e`,
              ],
              isAnswerEditable: true,
            },
            editPageTitle: i18n.t('business:Questions.travel.question3.subQuestion2.editTitle'),
            details: {},
            initiative: commutesInitiative,
            education: EDUCATION_ACTIVITY,
          },
        ],
      },
    ],
    education: EDUCATION,
  };
};
